/**
 * Menus hook
 * TanStack Query hooks for weekly menu viewing and editing
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getWeekMenu,
  createMenu,
  updateMenu,
  deleteMenu,
} from '@/lib/api/menus';
import { getAvailabilitySummary } from '@/lib/api/availability';
import type {
  GetWeekMenuQuery,
  CreateMenuRequest,
  UpdateMenuRequest,
  GetAvailabilitySummaryQuery,
} from '@/types/api';
import { menuKeys } from '@/lib/query-keys';

// Re-export for backwards compatibility
export { menuKeys };

/**
 * Hook to fetch weekly menu
 *
 * Features:
 * - Get menu for a specific week (defaults to current week)
 * - Returns 7-day structure with lunch/dinner meals
 * - Includes week date range
 * - Used by members to view organizer's menu plan
 *
 */
export function useWeekMenu(params?: GetWeekMenuQuery) {
  return useQuery({
    queryKey: menuKeys.week(params),
    queryFn: () => getWeekMenu(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Hook to fetch availability summary for menu editing
 *
 * Features:
 * - Get who's present for each day/meal in date range
 * - Used by organizers to plan menus based on attendance
 * - Includes user count for each slot
 *
 * @example
 * ```tsx
 * function MenuEditor() {
 *   const { data, isLoading } = useAvailabilitySummary({
 *     startDate: '2025-01-06',
 *     endDate: '2025-01-12',
 *   });
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       {data.summary.map(item => (
 *         <div key={`${item.date}-${item.mealType}`}>
 *           {item.date} {item.mealType}: {item.presentUsers.length} people
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAvailabilitySummary(params: GetAvailabilitySummaryQuery) {
  return useQuery({
    queryKey: menuKeys.availability(params),
    queryFn: () => getAvailabilitySummary(params),
    enabled: !!(params.startDate && params.endDate),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
}

export function useCreateMenu() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMenuRequest) => createMenu(data),
    onSuccess: () => {
      // Invalidate all week menu queries
      queryClient.invalidateQueries({
        queryKey: menuKeys.all,
      });
      // Invalidate menu suggestions cache
      queryClient.invalidateQueries({ queryKey: ['menu-suggestions'] });
    },
  });
}

export function useUpdateMenu() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateMenuRequest }) =>
      updateMenu(id, data),
    onSuccess: () => {
      // Invalidate all week menu queries
      queryClient.invalidateQueries({
        queryKey: menuKeys.all,
      });
      // Invalidate menu suggestions cache
      queryClient.invalidateQueries({ queryKey: ['menu-suggestions'] });
    },
  });
}

export function useDeleteMenu() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteMenu(id),
    onSuccess: () => {
      // Invalidate all week menu queries
      queryClient.invalidateQueries({
        queryKey: menuKeys.all,
      });
      // Invalidate menu suggestions cache
      queryClient.invalidateQueries({ queryKey: ['menu-suggestions'] });
    },
  });
}
